// Listener panel: where the engine is heard from (#41, docs/contracts/listeners.md).
//
// It lists the engine's listener positions and "direct" (the sources with no
// room), and keeps the choice in the view state's audio.listener (null for the
// engine's default). A running voice gets the change as its listener control,
// which crossfades to the new position; a voice that starts later starts there.

import { DIRECT, listenerIndex } from "../../../audio/listeners";
import { defineFeature } from "../../../registry";
import { el } from "../../controls/dom";
import { strings } from "./strings";

export default defineFeature({
  id: "listener",
  title: strings.title,
  slot: "panel",
  order: 20,
  mount({ root, state, audio }) {
    const table = audio.listenerTable;
    if (table === null) {
      root.append(el("p", { class: "panel-note" }, strings.none));
      return;
    }
    const byId = new Map(table.listeners.map((l) => [l.id, l]));
    const defaultName = byId.get(table.default)?.name ?? table.default;
    const fieldset = el("fieldset", { class: "panel-choices" }, el("legend", {}, strings.legend));
    const radios: [string | null, HTMLInputElement][] = [];
    const choice = (value: string | null, label: string, hint: string): void => {
      const input = el("input", {
        type: "radio",
        name: "listener",
        value: value ?? "",
        "data-listener": value ?? "default",
      });
      input.addEventListener("change", () => {
        if (input.checked) state.set({ audio: { listener: value } });
      });
      radios.push([value, input]);
      fieldset.append(el("label", { title: hint }, input, el("span", {}, label)));
    };
    choice(null, strings.default(defaultName), strings.defaultHint);
    for (const l of table.listeners) if (l.id !== DIRECT) choice(l.id, l.name, l.description);
    const direct = byId.get(DIRECT);
    if (direct !== undefined) choice(DIRECT, direct.name, direct.description);
    const description = el("p", { class: "panel-note", "data-listener-description": "" });
    root.append(fieldset, description, el("p", { class: "panel-note" }, strings.synthetic));

    // A voice that starts later starts at the chosen position.
    const previous = audio.startListener;
    audio.startListener = () => state.state.audio.listener;

    let sent: number | null = null;
    const render = (): void => {
      const id = state.state.audio.listener;
      // An id the engine does not have shows (and sounds) as its default.
      const known = id !== null && byId.has(id) ? id : null;
      for (const [value, input] of radios) input.checked = value === known;
      const index = listenerIndex(table, id);
      description.textContent = table.listeners[index]?.description ?? "";
      if (audio.status !== "on" || !audio.accepts("listener")) {
        sent = null;
        return;
      }
      // The voice started at the state's listener (startListener); send only changes.
      if (sent === null) sent = index;
      if (index !== sent) {
        audio.control("listener", index);
        sent = index;
      }
    };
    const offs = [state.subscribe(render), audio.onStatus(render)];
    render();
    return () => {
      for (const off of offs) off();
      audio.startListener = previous;
    };
  },
});
